import React, { useEffect, useState, useRef } from 'react'
import { 
  Card, Button, Form, Input, Checkbox, Typography, Space, 
  Tag, Divider, Tooltip, Slider, App, Select, Upload, Segmented, Image, Modal 
} from 'antd'
import { 
  VideoCameraOutlined, 
  PictureOutlined, 
  PlayCircleOutlined, 
  DownloadOutlined,
  HistoryOutlined,
  SoundOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  RocketOutlined,
  FireOutlined,
  CrownOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  DeleteOutlined,
  ReloadOutlined,
  BulbOutlined,
  CloseOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { apiClient } from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import { VideoModel, VideoGenerationType, VideoOrientation } from '@/types'
import { on as onEvent } from '@/services/events'
import { GlobalStyles, COMMON_STYLES } from '@/styles/globalStyles'
import '@/styles/global.css'
import { textService } from '@/services/textService'

const { Title, Paragraph, Text } = Typography
const { Dragger } = Upload
const { Option } = Select
const { TextArea } = Input

const VideoGenerationPage: React.FC = () => {
  const { message, modal } = App.useApp()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [generationType, setGenerationType] = useState<VideoGenerationType>('TEXT_TO_VIDEO')
  const [orientation, setOrientation] = useState<VideoOrientation>('PORTRAIT')
  const [hasAudio, setHasAudio] = useState(false)
  const [duration, setDuration] = useState(10)
  const [currentGeneration, setCurrentGeneration] = useState<any>(null)
  const [refImageFile, setRefImageFile] = useState<File | null>(null)
  const [refImageUrl, setRefImageUrl] = useState<string | null>(null)
  const [refImagePreviewUrl, setRefImagePreviewUrl] = useState<string | null>(null)  // 用于本地预览的URL
  const [uploadingImage, setUploadingImage] = useState(false)
  const [inlineError, setInlineError] = useState<string | null>(null)
  const [mobileTab, setMobileTab] = useState<'create' | 'result'>('create')
  const navigate = useNavigate()
  const [inspirations, setInspirations] = useState<any[]>([])
  const [loadingInspirations, setLoadingInspirations] = useState(false)
  const [videoModels, setVideoModels] = useState<any[]>([])
  const [loadingModels, setLoadingModels] = useState(false)
  const [videoProviders, setVideoProviders] = useState<any[]>([])
  const [loadingProviders, setLoadingProviders] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<string>('')
  const [promptValue, setPromptValue] = useState<string>('')
  const promptInputRef = useRef<any>(null)
  const [polishing, setPolishing] = useState(false)

  // 防重复点击保护
  const lastClickTimeRef = useRef(0)
  const MIN_CLICK_INTERVAL = 2000 // 2秒最小间隔

  // 加载视频服务商配置
  const loadProviders = async () => {
    try {
      setLoadingProviders(true)
      // 🔥 修复：使用公开API而不是管理员API
      const response = await apiClient.get('/video/models')
      
      if (response.success && response.data) {
        const { models } = response.data
        
        // 直接将API返回的模型转换为前端格式，并过滤启用的模型
        // 规范化 priority 为数字
        const normalized = models.map((model: any) => ({
          ...model,
          priority: Number(model.priority ?? 999)
        }))

        const allModels = normalized
          .filter((model: any) => model.enabled) // 🔥 修复：只显示启用的模型
          .map((model: any) => ({
            id: model.id,
            name: model.name,
            provider: model.provider,
            providerName: model.providerName,
            description: `${model.providerName} - ${model.name}`,
            cost: model.cost,
            enabled: model.enabled,
            priority: Number(model.priority ?? 999)  // 添加优先级字段，默认最低优先级（数值越小优先级越高：1 最高）
          }))
          .sort((a: any, b: any) => {
            // 按优先级从高到低排序（1 为最高，其次 2、3...）
            const priorityA = Number(a.priority ?? 999)
            const priorityB = Number(b.priority ?? 999)
            return priorityA - priorityB
          })

      // 调试输出排序结果
      console.log('📑 [视频页面] 排序后的模型顺序:', allModels.map((m:any) => `${m.providerName}:${m.name}#${m.priority}`).join(' | '))
      
      console.log(`✅ [视频页面] 模型配置加载成功: ${allModels.length} 个启用模型 (总共 ${models.length} 个)`)
      
      setVideoModels(allModels)
      
      // 归一化当前选择：将旧格式（仅模型名）转换为 provider-model，优先取最高优先级
      if (allModels.length > 0) {
        let needSetDefault = false
        if (selectedModel) {
          const exists = allModels.some((m: any) => m.id === selectedModel)
          if (!exists) {
            if (!String(selectedModel).includes('-')) {
              const fallback = allModels.find((m: any) => m.name === selectedModel)
              if (fallback) {
                console.log('🔧 [视频页面] 归一化旧模型值 ->', selectedModel, '→', `${fallback.id} (${fallback.name} · ${fallback.providerName})`)
                setSelectedModel(fallback.id)
              } else {
                needSetDefault = true
              }
            } else {
              needSetDefault = true
            }
          }
        } else {
          needSetDefault = true
        }

        if (needSetDefault) {
          const top = allModels[0]
          setSelectedModel(top.id)
          console.log(`🔧 [视频页面] 设置默认模型: ${top.id} (${top.name} · ${top.providerName})`)
        }
      } else if (allModels.length === 0) {
        console.warn('⚠️ [视频页面] 没有启用的模型')
        setSelectedModel('')
      }
      } else {
        console.warn('⚠️ [视频页面] 后端无服务商配置，请管理员在控制台配置API服务商')
        setVideoModels([])
        setSelectedModel('')
      }
    } catch (error) {
      console.error('❌ [视频页面] 加载服务商配置失败:', error)
      // 失败时不使用默认配置，完全依赖管理控制台配置
      setVideoModels([])
      setSelectedModel('')
    } finally {
      setLoadingProviders(false)
    }
  }


  // 从localStorage加载用户设置
  useEffect(() => {
    const savedSettings = localStorage.getItem('videoGenerationSettings')
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings)
        if (settings.selectedModel) setSelectedModel(settings.selectedModel)
        if (settings.orientation) setOrientation(settings.orientation)
        if (settings.duration) setDuration(settings.duration)
                    if (settings.prompt) setPromptValue(settings.prompt)
      } catch (error) {
        console.error('Failed to load saved settings:', error)
      }
    }
  }, [])

  // 加载服务商配置
  useEffect(() => {
    loadProviders()
  }, [])

  // 加载热门创作灵感
  useEffect(() => {
    loadInspirations()
    
    // 每30秒自动刷新灵感
    const refreshInterval = setInterval(() => {
      console.log('🔄 [视频页面] 自动刷新灵感')
      loadInspirations()
    }, 30000)
    
    return () => clearInterval(refreshInterval)
  }, [])

  // 当页面重新可见时刷新灵感
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👀 [视频页面] 页面重新可见，刷新灵感')
        loadInspirations()
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  const loadInspirations = async () => {
    try {
      setLoadingInspirations(true)
      const response = await apiClient.get('/inspiration/public', {
        params: {
          type: 'VIDEO',
          limit: 6
        }
      })
      
      // apiClient 已经处理过响应，response 就是 {success, data} 格式
      if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
        const items = response.data.map((item: any) => ({
          ...item,
          tags: typeof item.tags === 'string' ? JSON.parse(item.tags) : item.tags
        }))
        setInspirations(items)
        console.log('✅ [视频页面] 灵感加载成功:', items.length, '个')
      } else {
        // 数据为空时使用默认示例
        console.warn('⚠️ [视频页面] 后端无灵感数据，使用默认示例')
        setInspirations([
          { id: '1', emoji: '🌅', title: '海滩日落', prompt: '日落时分的海滩，海浪轻拍沙滩，海鸥飞过，金色阳光洒在水面上，电影级慢镜头' },
          { id: '2', emoji: '🌃', title: '未来城市', prompt: '未来城市夜景，霓虹灯闪烁，飞行汽车穿梭，赛博朋克风格，雨夜反光' },
          { id: '3', emoji: '🐼', title: '可爱熊猫', prompt: '一只可爱的小熊猫在竹林里玩耍，咀嚼竹叶，憨态可掬，4K高清，自然纪录片风格' },
          { id: '4', emoji: '📹', title: '街头vlog', prompt: '城市街头手持镜头行走拍摄，快速剪辑，转场流畅，音乐节奏感强' },
          { id: '5', emoji: '🌸', title: '樱花飘落', prompt: '樱花树下，花瓣随风飘落，阳光透过树叶洒下光斑，唯美日系风格' }
        ])
      }
    } catch (error) {
      console.error('❌ [视频页面] 加载灵感失败:', error)
      // 失败时也使用默认示例
      setInspirations([
        { id: '1', emoji: '🌅', title: '海滩日落', prompt: '日落时分的海滩，海浪轻拍沙滩，海鸥飞过，金色阳光洒在水面上，电影级慢镜头' },
        { id: '2', emoji: '🌃', title: '未来城市', prompt: '未来城市夜景，霓虹灯闪烁，飞行汽车穿梭，赛博朋克风格，雨夜反光' },
        { id: '3', emoji: '🐼', title: '可爱熊猫', prompt: '一只可爱的小熊猫在竹林里玩耍，咀嚼竹叶，憨态可掬，4K高清，自然纪录片风格' },
        { id: '4', emoji: '📹', title: '街头vlog', prompt: '城市街头手持镜头行走拍摄，快速剪辑，转场流畅，音乐节奏感强' },
        { id: '5', emoji: '🌸', title: '樱花飘落', prompt: '樱花树下，花瓣随风飘落，阳光透过树叶洒下光斑，唯美日系风格' }
      ])
    } finally {
      setLoadingInspirations(false)
    }
  }

  // 保存用户设置到localStorage
  const saveSettings = () => {
    const settings = {
      selectedModel,
      orientation,
      duration,
      prompt: promptValue
    }
    localStorage.setItem('videoGenerationSettings', JSON.stringify(settings))
  }

  // 当设置改变时保存
  useEffect(() => {
    saveSettings()
  }, [selectedModel, orientation, duration])
  
  // 定期检查生成任务状态（备用机制）
  const errorCountRef = useRef(0)
  
  useEffect(() => {
    if (!currentGeneration || 
        currentGeneration.status?.toLowerCase() === 'completed' || 
        currentGeneration.status?.toLowerCase() === 'failed' || 
        currentGeneration.outputVideo) {
      console.log('🛑 [定期检查] 停止轮询，状态:', currentGeneration?.status)
      // 重置错误计数
      errorCountRef.current = 0
      return
    }
    
    const MAX_ERROR_COUNT = 3 // 连续3次错误后停止轮询
    
    const checkStatus = async () => {
      try {
        console.log('🔄 [定期检查] 查询任务状态:', currentGeneration.id)
        const response = await apiClient.get(`/video/generations/${currentGeneration.id}`)
        
        // 重置错误计数（成功时）
        errorCountRef.current = 0
        
        if (response.success && response.data?.videoGeneration) {
          const gen = response.data.videoGeneration
          console.log('📊 [定期检查] 任务状态:', gen.status)
          console.log('📊 [定期检查] 错误信息:', gen.errorMessage)
          
          if (gen.status?.toLowerCase() === 'completed' && gen.outputVideo) {
            console.log('✅ [定期检查] 任务已完成，更新状态')
            setCurrentGeneration({
              ...currentGeneration,
              status: 'completed',
              outputVideo: gen.outputVideo
            })
          } else if (gen.status?.toLowerCase() === 'failed') {
            console.log('❌ [定期检查] 任务失败，更新状态')
            console.log('❌ [定期检查] 错误信息:', gen.errorMessage)
            setCurrentGeneration({
              ...currentGeneration,
              status: 'failed',
              errorMessage: gen.errorMessage
            })
            
            // 显示友好的错误提示
            if (gen.errorMessage && (
              gen.errorMessage.includes('人物照片') ||
              gen.errorMessage.includes('内容审核') ||
              gen.errorMessage.includes('photorealistic people')
            )) {
              console.log('📢 [定期检查] 显示内容审核错误提示（内联）:', gen.errorMessage)
              console.log('📢 [定期检查] 设置 inlineError 状态:', gen.errorMessage)
              setInlineError(gen.errorMessage)
            } else {
              console.log('📢 [定期检查] 显示通用错误提示:', gen.errorMessage || '视频生成失败')
              message.error({
                content: gen.errorMessage || '视频生成失败',
                key: `error-${currentGeneration.id}`
              })
            }
          }
        }
      } catch (error: any) {
        errorCountRef.current++
        console.error(`❌ [定期检查] 查询失败 (${errorCountRef.current}/${MAX_ERROR_COUNT}):`, error)
        
        // 检查是否是服务器错误（500, 502, 503等）
        const isServerError = error?.response?.status >= 500 && error?.response?.status < 600
        
        if (errorCountRef.current >= MAX_ERROR_COUNT) {
          console.error(`🛑 [定期检查] 连续${MAX_ERROR_COUNT}次错误，停止轮询`)
          if (isServerError) {
            message.warning({
              content: '服务器暂时无法响应，请稍后刷新页面查看状态',
              key: `server-error-${currentGeneration.id}`,
              duration: 5
            })
          }
          return // 停止定时器
        }
        
        // 如果是服务器错误，显示警告（但不阻止轮询，直到达到最大错误次数）
        if (isServerError && errorCountRef.current === 1) {
          console.warn('⚠️  [定期检查] 服务器错误，将减少轮询频率')
        }
      }
    }
    
    // 每10秒检查一次
    const timer = setInterval(() => {
      checkStatus().then(() => {
        // 如果错误次数达到上限，清除定时器
        if (errorCountRef.current >= MAX_ERROR_COUNT) {
          clearInterval(timer)
        }
      })
    }, 10000)
    
    return () => clearInterval(timer)
  }, [currentGeneration])
  // 实时更新当前生成任务状态（若打开详情时）
  useEffect(() => {
    const handler = (p: any) => {
      console.log('📺 [视频生成页面] 收到视频更新事件:', p)
      console.log('🔍 [SSE] 当前任务ID:', currentGeneration?.id, '事件任务ID:', p.id)
      console.log('🔍 [SSE] 事件状态:', p.status, '错误信息:', p.errorMessage)
      
      if (currentGeneration?.id === p.id) {
        console.log('✅ [视频生成页面] 匹配到当前生成任务，更新状态')
        console.log('📊 [视频生成页面] 事件数据:', { 
          status: p.status, 
          outputVideo: p.outputVideo,
          hasOutputVideo: !!p.outputVideo,
          errorMessage: p.errorMessage
        })
        
        setCurrentGeneration(prev => {
          const updated = { 
            ...prev, 
            ...p,
            // 确保状态是小写（统一格式）
            status: p.status?.toLowerCase() || prev.status,
            // 确保错误信息正确传递
            errorMessage: p.errorMessage || prev.errorMessage
          }
          console.log('🔄 [视频生成页面] 更新后的状态:', updated)
          
          // 如果是失败状态，显示友好的错误提示
          if (updated.status === 'failed' && p.errorMessage) {
            console.log('📢 [SSE事件] 检测到失败状态，错误信息:', p.errorMessage)
            console.log('📢 [SSE事件] 更新后的状态:', updated.status)
            if (p.errorMessage.includes('人物照片') || 
                p.errorMessage.includes('内容审核') ||
                p.errorMessage.includes('photorealistic people')) {
              console.log('📢 [SSE事件] 显示内容审核错误提示（内联）:', p.errorMessage)
              console.log('📢 [SSE事件] 设置 inlineError 状态:', p.errorMessage)
              setInlineError(p.errorMessage)
            } else {
              console.log('📢 [SSE事件] 显示通用错误提示:', p.errorMessage || '视频生成失败')
              message.error({
                content: p.errorMessage || '视频生成失败',
                key: `error-${p.id}`
              })
            }
          } else {
            console.log('🔍 [SSE事件] 非失败状态或无误信息:', {
              status: updated.status,
              hasErrorMessage: !!p.errorMessage,
              errorMessage: p.errorMessage
            })
          }
          
          return updated
        })
      } else {
        console.log('⚠️  [视频生成页面] 任务ID不匹配', {
          currentId: currentGeneration?.id,
          eventId: p.id
        })
      }
    }
    onEvent('video_updated', handler)
  }, [currentGeneration])

  // 删除参考图
  const handleRemoveImage = () => {
    setRefImageFile(null)
    setRefImageUrl(null)
    setRefImagePreviewUrl(null)
    setGenerationType('TEXT_TO_VIDEO')
    setInlineError(null) // 清空内联错误提示
    message.success('已移除参考图')
  }

  // 处理模型配置，添加UI相关属性
  const getModelWithUI = (model: any) => {
    if (!model) return null
    
    const icons: Record<string, any> = {
      'SORA2': <RocketOutlined />,
      'SORA2_PRO': <FireOutlined />,
      'SORA2_ADVANCED': <CrownOutlined />
    }
    const colors: Record<string, string> = {
      'SORA2': '#3b82f6',
      'SORA2_PRO': '#f59e0b',
      'SORA2_ADVANCED': '#8b5cf6'
    }
    const tags: Record<string, { text: string, color: string }> = {
      'SORA2': { text: '推荐', color: 'blue' },
      'SORA2_PRO': { text: '专业', color: 'orange' },
      'SORA2_ADVANCED': { text: '旗舰', color: 'purple' }
    }
    
    return {
      ...model,
      icon: icons[model.id] || <RocketOutlined />,
      color: colors[model.id] || '#3b82f6',
      tag: tags[model.id]?.text,
      tagColor: tags[model.id]?.color
    }
  }

  const currentModelInfo = getModelWithUI(videoModels.find(model => model.id === selectedModel))

  // 🔥 新增：计算视频生成成本
  const calculateVideoCost = (): number => {
    const selectedModelData = videoModels.find(m => m.id === selectedModel);
    if (!selectedModelData) return 20; // 默认成本
    
    return selectedModelData.cost || 20;
  };

  const handleGenerate = async (values: any) => {
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    
    // 检查是否在最小间隔内
    if (timeSinceLastClick < MIN_CLICK_INTERVAL && lastClickTimeRef.current > 0) {
      const remainingTime = Math.ceil((MIN_CLICK_INTERVAL - timeSinceLastClick) / 1000);
      message.warning(`请稍候 ${remainingTime} 秒后再试`);
      return;
    }
    
    // 更新最后点击时间
    lastClickTimeRef.current = now;
    
    const startTime = Date.now();
    console.log('\n' + '='.repeat(80));
    console.log('📹 [前端] 视频生成请求开始');
    console.log('⏰ 时间:', new Date().toISOString());
    console.log('='.repeat(80));
    
    // 清空内联错误提示
    setInlineError(null)
    
    // 注释：全局错误监听器已移除，避免与 SSE 事件处理器重复
    
    // 🔥 新增：检查积分是否足够
    const { user } = useAuthStore.getState();
    const cost = calculateVideoCost();
    
    if (!user || (user.pointsBalance < cost && user.credits < cost)) {
      message.error(`积分不足！需要 ${cost} 积分，当前余额: ${user?.pointsBalance || 0} 积分`);
      console.error(`❌ 积分不足: 需要 ${cost}, 当前 ${user?.pointsBalance || 0}`);
      return;
    }
    
    console.log(`✅ 积分检查通过: 需要 ${cost}, 当前 ${user.pointsBalance}`);
    
    if (generationType === 'IMAGE_TO_VIDEO' && !refImageUrl) {
      console.warn('⚠️  [验证失败] 图生视频需要参考图片');
      message.warning('请上传参考图片')
      return
    }
    
    // 验证 refImageUrl 格式（如果是图生视频类型）
    if (generationType === 'IMAGE_TO_VIDEO' && refImageUrl) {
      try {
        const url = new URL(refImageUrl)
        if (!url.protocol.startsWith('http')) {
          console.error('❌ [验证失败] refImageUrl 不是有效的 HTTP(S) URL:', refImageUrl)
          message.error('参考图 URL 格式错误，请重新上传')
          return
        }
      } catch (e) {
        console.error('❌ [验证失败] refImageUrl 不是有效的 URL:', refImageUrl)
        message.error('参考图 URL 格式错误，请重新上传')
        return
      }
    }
    
    setLoading(true)
    
    try {
      const payload: any = {
        prompt: promptValue || values.prompt,
        model: selectedModel,
        type: generationType,
        orientation,
        hasAudio,
        duration
      }
      
      // 如果有参考图URL，添加到payload（确保不是空字符串）
      if (refImageUrl && refImageUrl.trim() !== '') {
        payload.inputImage = refImageUrl
        console.log('📸 [前端] 包含参考图URL:', refImageUrl);
        console.log('📸 [前端] 参考图URL类型:', typeof refImageUrl);
        console.log('📸 [前端] 参考图URL是否为blob:', refImageUrl.startsWith('blob:'));
      }
      
      console.log('📊 请求参数:', {
        model: selectedModel,
        type: generationType,
        orientation,
        hasAudio,
        duration,
        inputImage: refImageUrl || '无',
        promptLength: (promptValue || '').length,
        promptPreview: (promptValue || '').substring(0, 100) + ((promptValue || '').length > 100 ? '...' : '')
      });
      
      console.log('📤 发送 API 请求: POST /video/generate');
      console.log('📦 完整 payload:', JSON.stringify(payload, null, 2));
      const apiStartTime = Date.now();
      
      const res = await apiClient.post('/video/generate', payload)
      const apiTime = Date.now() - apiStartTime;
      
      console.log(`✅ API 响应成功 (耗时: ${apiTime}ms)`);
      console.log('📥 响应数据:', {
        success: res.success,
        hasVideoGeneration: !!res.data?.videoGeneration,
        generationId: res.data?.videoGeneration?.id,
        status: res.data?.videoGeneration?.status,
        cost: res.data?.videoGeneration?.cost
      });
      
      if (res.success && res.data?.videoGeneration) {
        setCurrentGeneration({...res.data.videoGeneration, status: 'GENERATING'})
        message.success('已创建生成任务，正在生成中...')
        console.log('✅ 生成任务已创建, ID:', res.data.videoGeneration.id);
        
        // 移动端自动切换到结果标签
        setMobileTab('result')
        
        // 本地乐观扣费，SSE 随后会以绝对值校正
        try {
          const { user, updateUser } = useAuthStore.getState()
          if (user?.pointsBalance !== undefined && (res.data.videoGeneration?.cost !== undefined)) {
            const oldCredits = user.pointsBalance || 0;
            const cost = res.data.videoGeneration.cost || 0;
            const newCredits = Math.max(0, oldCredits - cost);
            updateUser({ pointsBalance: newCredits })
            console.log(`💰 本地积分更新: ${oldCredits} → ${newCredits} (扣除: ${cost})`);
          }
        } catch (creditsError) {
          console.error('❌ 本地积分更新失败:', creditsError);
        }
        
        const totalTime = Date.now() - startTime;
        console.log(`🎉 请求处理完成 (总耗时: ${totalTime}ms)`);
        console.log('='.repeat(80) + '\n');
      } else {
        throw new Error(res.message || '生成创建失败')
      }
    } catch (e: any) {
      const totalTime = Date.now() - startTime;
      console.error('\n' + '='.repeat(80));
      console.error('❌ [前端] 视频生成请求失败');
      console.error('⏱️  失败前耗时:', totalTime + 'ms');
      console.error('='.repeat(80));
      
      console.error('🔍 错误详情:');
      console.error('  错误类型:', e?.constructor?.name || 'Unknown');
      console.error('  错误消息:', e?.message || 'Unknown error');
      
      if (e?.response) {
        console.error('📡 API 响应错误:');
        console.error('  状态码:', e.response.status);
        console.error('  状态文本:', e.response.statusText);
        console.error('  响应数据:', e.response.data);
        
        // 如果是验证错误，显示详细的字段错误信息
        if (e.response.status === 400 && e.response.data?.errors) {
          const errorMessages = e.response.data.errors.map((err: any) => 
            `${err.field}: ${err.message}`
          ).join('；');
          message.error(`输入验证失败：${errorMessages}`);
          return;
        }
        
        // 如果有错误消息，显示它
        if (e.response.data?.message) {
          message.error(e.response.data.message);
          return;
        }
      }
      
      if (e?.stack) {
        console.error('📚 错误堆栈:', e.stack);
      }
      
      console.error('='.repeat(80) + '\n');
      
      // 检测积分不足错误（处理 500 错误中的积分不足情况）
      let errorMessage = e?.message || '生成失败，请重试';
      let isInsufficientCredits = false;
      let allMessages: string[] = [];
      
      // 收集所有可能的错误消息
      if (e?.message) {
        allMessages.push(e.message);
      }
      
      if (e?.response?.data?.message) {
        allMessages.push(e.response.data.message);
      }
      
      if (e?.response?.data?.error) {
        allMessages.push(String(e.response.data.error));
      }
      
      // 合并所有消息进行检测
      const combinedMessage = allMessages.join(' ').toLowerCase();
      
      // 检测积分不足关键词
      if (combinedMessage.includes('insufficient credits') ||
          combinedMessage.includes('积分不足') ||
          combinedMessage.includes('credits and points') ||
          (combinedMessage.includes('insufficient') && combinedMessage.includes('credit'))) {
        isInsufficientCredits = true;
      }
      
      // 尝试解析嵌套的 JSON 字符串（如：{"code":-1,"msg":"insufficient credits"}）
      if (!isInsufficientCredits) {
        try {
          const messageStr = allMessages.join(' ');
          const jsonMatch = messageStr.match(/\{[^}]+\}/);
          if (jsonMatch) {
            const nestedJson = JSON.parse(jsonMatch[0]);
            if (nestedJson && nestedJson.msg) {
              const nestedMsg = String(nestedJson.msg).toLowerCase();
              if (nestedMsg.includes('insufficient credits') ||
                  nestedMsg.includes('积分不足') ||
                  nestedMsg.includes('credit')) {
                isInsufficientCredits = true;
              }
            }
          }
        } catch (parseError) {
          // 解析失败，继续使用原始消息
        }
      }
      
      // 显示错误提示
      if (isInsufficientCredits) {
        Modal.confirm({
          title: '积分不足',
          content: '您的积分不足，无法生成视频。是否前往充值？',
          okText: '去充值',
          cancelText: '取消',
          onOk: () => {
            // 导航到充值页面
            navigate('/payment');
          }
        });
      } else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen w-full relative">
      {/* 内容区域 - 应用统一样式 */}
      <div className="unified-container relative z-10 py-2 md:py-6">
        <motion.div
          {...COMMON_STYLES.pageEnter}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col"
        >
          {/* 头部设计 - 左右结构 */}
          <div className="w-full">
            <motion.div
              {...COMMON_STYLES.pageEnter}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            >
              {/* 左侧：标题和副标题 */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">🎬</span>
                  <h1 className="unified-title-h1 mb-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                    AI 视频生成
                  </h1>
                </div>
                <Paragraph className="unified-text-secondary text-base md:ml-12 !mb-0">
                  用文字描述你的创意，让AI为你创造惊艳的视频内容
                </Paragraph>
              </div>
              
              {/* 右侧：特色标签 */}
              <div className="flex flex-wrap gap-2 md:justify-end">
                <Tag icon={<ThunderboltOutlined />} className="unified-tag bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700">
                秒级生成
              </Tag>
                <Tag icon={<FireOutlined />} className="unified-tag bg-gradient-to-r from-orange-50 to-orange-100 text-orange-700">
                高清画质
              </Tag>
                <Tag icon={<CheckCircleOutlined />} className="unified-tag bg-gradient-to-r from-green-50 to-green-100 text-green-700">
                无需技能
              </Tag>
            </div>
            </motion.div>
          </div>


          {/* 主内容区 - 左右2:1布局 */}
          <div className="w-full h-full">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch h-full">
              {/* 左侧：输入窗口 - 占2列 (2/3) */}
              <div className="lg:col-span-2">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-6"
                >
                {/* 移动端顶部装饰 */}
                <div className="md:hidden bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-1.5 w-full rounded-full mb-6"></div>

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleGenerate}
                  className="flex flex-col"
                >
                  {/* 参考图预览卡片 - 功能暂未实现，已隐藏 */}
                  {false && refImageFile && (
                    <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border-2 border-blue-200 shadow-md hover:shadow-lg transition-all duration-300">
                      <div className="flex items-center justify-between mb-2">
                        <Text className="text-sm font-bold text-blue-700 flex items-center gap-2">
                          📸 参考图片
                        </Text>
                        <div className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white rounded-full text-xs font-medium">
                          <span>✓</span>
                          <span>已上传</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="relative w-20 h-20 flex-shrink-0">
                          <img 
                            src={refImagePreviewUrl || URL.createObjectURL(refImageFile)} 
                            alt="参考图" 
                            className="w-full h-full object-cover rounded-xl shadow-md"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <Text className="block text-sm font-semibold truncate text-gray-800">{refImageFile.name}</Text>
                          <Text type="secondary" className="text-xs text-gray-500">
                            {(refImageFile.size / 1024).toFixed(2)} KB
                          </Text>
                          {refImageUrl && (
                            <Text type="secondary" className="text-xs text-green-600 block truncate" title={refImageUrl}>
                              ✓ 已上传到云端
                            </Text>
                          )}
                        </div>
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            // 清理blob URL防止内存泄漏
                            if (refImagePreviewUrl) {
                              URL.revokeObjectURL(refImagePreviewUrl);
                            }
                            setRefImageFile(null);
                            setRefImageUrl(null);
                            setRefImagePreviewUrl(null);
                            setGenerationType('TEXT_TO_VIDEO');
                          }}
                          className="rounded-xl bg-red-50 border-red-200 text-red-600 hover:bg-red-100 hover:border-red-300 transition-all duration-200 shadow-sm"
                        >
                          删除
                        </Button>
                              </div>
                            </div>
                  )}

                  {/* 热门创作灵感卡片 - 从API动态加载 */}
                  <div className="mb-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border-2 border-blue-200 shadow-md hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <BulbOutlined className="text-lg text-blue-600" />
                        <Text className="text-sm font-bold text-blue-700">热门创作灵感</Text>
                        {inspirations.length > 0 && (
                          <Tag color="blue" className="text-xs">{inspirations.length}个</Tag>
                        )}
                      </div>
                      <Text className="text-xs text-blue-600">点击即可使用 →</Text>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2 relative z-10 inspiration-scroll inspiration-scroll-blue hide-scrollbar">
                      {inspirations.length > 0 ? (
                        inspirations.map((item: any) => (
                          <button
                            key={item.id}
                            type="button"
                            role="button"
                            tabIndex={0}
                            className="inline-flex items-center px-3 py-2 text-xs bg-white hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 text-gray-700 hover:text-blue-700 rounded-xl cursor-pointer transition-all duration-200 border border-blue-200 hover:border-blue-300 shadow-sm hover:shadow-md font-medium hover:scale-105 relative z-10 pointer-events-auto whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px] flex-shrink-0"
                            onClick={async () => {
                              setMobileTab('create')
                              setPromptValue(item.prompt)
                              try { form.setFieldsValue({ prompt: item.prompt }) } catch {}
                              try { form.scrollToField('prompt', { block: 'center' }) } catch {}
                              setTimeout(() => { try { promptInputRef?.current?.focus?.() } catch {} }, 0)
                              message.success(`已应用"${item.title}"灵感 ✨`)
                              // 记录使用次数
                              try {
                                await apiClient.post(`/inspiration/${item.id}/use`)
                              } catch (error) {
                                console.error('记录使用失败:', error)
                              }
                            }}
                            title={`${item.title}\n\n${item.prompt.substring(0, 100)}...`}
                          >
                            {item.title}
                          </button>
                        ))
                      ) : (
                        <Text type="secondary" className="text-xs">
                          {loadingInspirations ? '加载中...' : '暂无灵感，请在管理后台添加'}
                        </Text>
                      )}
                    </div>
                  </div>

                  {/* 主输入区域卡片 */}
                  <div className="relative mb-4 bg-gray-50 rounded-xl p-4 border-2 border-gray-200 hover:border-blue-300 focus-within:border-blue-400 transition-all duration-300 shadow-md hover:shadow-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                      <span className="text-lg">✍️</span>
                      <Text className="text-sm font-bold text-gray-700">创作描述</Text>
                      </div>
                      <Button
                        type="link"
                        size="small"
                        icon={<BulbOutlined />}
                        loading={polishing}
                        onClick={async () => {
                          const currentPrompt = form.getFieldValue('prompt');
                          if (!currentPrompt || !currentPrompt.trim()) {
                            message.warning('请先输入描述内容');
                            return;
                          }
                          
                          try {
                            setPolishing(true);
                            const response = await textService.polishPrompt(currentPrompt, 'video');
                            if (response.success) {
                              form.setFieldsValue({ prompt: response.data.polishedText });
                              setPromptValue(response.data.polishedText);
                              
                              const cost = response.data?.cost || 0;
                              message.success(cost > 0 ? `润色成功，消耗 ${cost} 积分` : '润色成功');
                              
                              // 🔥 修复：润色成功后立即刷新用户积分（延迟一下确保后端事件已发送）
                              const { refreshUser } = useAuthStore.getState();
                              // 立即刷新一次
                              try {
                                await refreshUser();
                                console.log('✅ [VideoPage] 润色成功，用户积分已刷新');
                              } catch (refreshError) {
                                console.warn('⚠️ [VideoPage] 刷新用户积分失败:', refreshError);
                              }
                              // 延迟再刷新一次，确保后端事件处理完成
                              setTimeout(async () => {
                                try {
                                  await refreshUser();
                                  console.log('✅ [VideoPage] 延迟刷新用户积分完成');
                                } catch (refreshError) {
                                  console.warn('⚠️ [VideoPage] 延迟刷新用户积分失败:', refreshError);
                                }
                              }, 500);
                            }
                          } catch (error: any) {
                            message.error(error.response?.data?.message || '润色失败');
                          } finally {
                            setPolishing(false);
                          }
                        }}
                        className="text-blue-500 hover:text-blue-600"
                      >
                        {polishing ? '文案润色中...' : '文案润色'}
                      </Button>
                    </div>
                  <Form.Item
                    name="prompt"
                    rules={[{ required: true, message: '请输入视频描述' }]}
                        className="mb-0"
                  >
                    <div className="relative">
                        <TextArea
                          ref={promptInputRef}
                          rows={8}
                          placeholder="描述你想要的视频内容...（比如：一只可爱的小猫在花园里玩耍，阳光明媚，4K高清画质）"
                          className="border-0 shadow-none resize-none focus:shadow-none bg-white text-gray-700 placeholder-gray-400 rounded-xl"
                          style={{ 
                            resize: 'none',
                            boxShadow: 'none',
                            border: 'none',
                            outline: 'none',
                            paddingBottom: '60px',
                            paddingRight: '80px',  // 右侧留出空间给生成按钮
                            fontSize: '15px',
                            background: 'white',
                            lineHeight: '1.8',
                            padding: '16px',
                            paddingLeft: '104px'  // 放大上传区后增加左内边距（64px 图标 + 40px 间距）
                          }}
                          value={promptValue}
                          onChange={(e) => {
                            setPromptValue(e.target.value)
                            try { form.setFieldsValue({ prompt: e.target.value }) } catch {}
                          }}
                          maxLength={2000}
                          showCount={false}
                        />
                        
                        {/* 输入框内左上角 - 上传/预览区域 */}
                        <div className="absolute top-2 left-2 z-10">
                          <div className="w-16 h-16 border border-gray-200 rounded-md overflow-hidden relative group">
                            {!refImageFile ? (
                              // 未上传状态 - 显示上传区域
                              <Upload
                                className="!m-0 !p-0 w-full h-full"
                                style={{ width: '100%', height: '100%', display: 'block' }}
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                showUploadList={false}
                                beforeUpload={async (file) => {
                                  // 文件格式检查
                                  const isImage = file.type.startsWith('image/')
                                  if (!isImage) {
                                    message.error('只能上传图片文件（JPG/PNG/WEBP/GIF）')
                                    return false
                                  }
                                  
                                  // 文件大小检查（5MB）
                                  const isLt5M = file.size / 1024 / 1024 < 5
                                  if (!isLt5M) {
                                    message.error('图片大小不能超过 5MB')
                                    return false
                                  }

                                  try {
                                    setUploadingImage(true)
                                    message.loading('正在上传参考图...', 0)
                                    
                                    // 创建 FormData
                                    const formData = new FormData()
                                    formData.append('file', file)
                                    
                                    // 上传到云端
                                    const response = await apiClient.post('/video/upload-image', formData, {
                                      headers: {
                                        'Content-Type': 'multipart/form-data'
                                      },
                                      timeout: 120000
                                    })
                                    
                                    message.destroy()
                                    
                                    if (response.success) {
                                      setRefImageFile(file)
                                      setRefImageUrl(response.data.url)
                                      setRefImagePreviewUrl(URL.createObjectURL(file))
                                      setGenerationType('IMAGE_TO_VIDEO')
                                      message.success('参考图上传成功！')
                                      console.log('✅ [参考图上传成功]:', response.data.url)
                                    } else {
                                      message.error(response.message || '上传失败')
                                    }
                                  } catch (error: any) {
                                    message.destroy()
                                    console.error('上传失败:', error)
                                    message.error('上传失败: ' + (error?.response?.data?.message || error?.message || '网络错误'))
                                  } finally {
                                    setUploadingImage(false)
                                  }
                                  
                                  return false
                                }}
                              >
                                <div className="w-full h-full bg-gray-50 hover:bg-blue-50 cursor-pointer transition-all duration-200 flex items-center justify-center !m-0 !p-0">
                                  {uploadingImage ? (
                                    <div className="flex flex-col items-center justify-center text-blue-500 gap-0.5">
                                      <div className="animate-spin text-2xl leading-none">⏳</div>
                                      <div className="text-[11px] leading-none">上传中</div>
                                    </div>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center gap-0.5">
                                      <div className="text-3xl text-gray-600 group-hover:text-blue-500 transition-colors font-semibold leading-none">
                                        +
                                      </div>
                                      <div className="text-[11px] text-gray-400 leading-none">
                                        上传参考图
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </Upload>
                            ) : (
                              // 已上传状态 - 显示缩略图
                              <>
                                <img 
                                  src={refImagePreviewUrl} 
                                  alt="参考图预览" 
                                  className="w-full h-full object-cover cursor-pointer"
                                  onClick={() => {
                                    // 打开紧凑无留白的预览
                                    modal.info({
                                      title: null,
                                      centered: true,
                                      icon: null,
                                      content: (
                                        <div className="relative inline-block bg-black/20 border border-white/30 rounded-md" style={{ width: 'fit-content' }}>
                                          <Button
                                            type="text"
                                            size="small"
                                            onClick={() => Modal.destroyAll()}
                                            className="!m-0 !p-0 absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/70 text-white flex items-center justify-center"
                                            icon={<CloseOutlined style={{ fontSize: 16 }} />}
                                          />
                                          <img
                                            src={refImagePreviewUrl}
                                            alt="参考图预览"
                                            className="object-contain rounded-md"
                                            style={{
                                              background: 'transparent',
                                              display: 'block',
                                              maxWidth: '95vw',
                                              maxHeight: '90vh'
                                            }}
                                          />
                                        </div>
                                      ),
                                      width: undefined,
                                      footer: null,
                                      styles: {
                                        body: { padding: 0, background: 'transparent' },
                                        content: { padding: 0, background: 'transparent' }
                                      },
                                      bodyStyle: { padding: 0 },
                                      maskClosable: true
                                    })
                                  }}
                                />
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<CloseOutlined />}
                                  className="absolute -top-1 -right-1 w-4 h-4 min-w-4 bg-gray-200 text-gray-700 hover:bg-gray-300 rounded-full flex items-center justify-center opacity-40 group-hover:opacity-100 transition-opacity duration-200 shadow-lg text-[10px] leading-none"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRemoveImage()
                                  }}
                                />
                                <div className="absolute top-0 left-16 bg-gray-800 text-white text-xs px-2 py-0.5 rounded opacity-0 group-hover:opacity-95 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-20">
                                  ✓ 已上传
                                </div>
                              </>
                            )}
                          </div>
                          
                          {/* 内联错误提示 */}
                          {inlineError && (
                            <div className="mt-1 text-xs text-red-500 leading-snug">{inlineError}</div>
                          )}
                        </div>
                    </div>
                  </Form.Item>
                      
                      {/* 生成按钮 */}
                      <div className="absolute bottom-8 right-8">
                        {(() => {
                          const { user } = useAuthStore.getState();
                          const cost = calculateVideoCost();
                          const isPointsInsufficient = !user || (user.pointsBalance < cost && user.credits < cost);
                          
                          return (
                            <>
                              {isPointsInsufficient && (
                                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                  <div className="flex items-center gap-2 text-yellow-800">
                                    <span>⚠️</span>
                                    <span className="text-sm font-medium">积分不足</span>
                                  </div>
                                  <div className="text-xs text-yellow-700 mt-1">
                                    生成需要 {cost} 积分，当前余额: {user?.pointsBalance || 0} 积分
                                  </div>
                                  <Button 
                                    size="small" 
                                    type="primary" 
                                    onClick={() => navigate('/payment')}
                                    className="mt-2"
                                  >
                                    立即充值
                                  </Button>
                                </div>
                              )}
                              
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                          disabled={loading || uploadingImage || isPointsInsufficient}
                          className={`unified-button h-12 px-6 py-3 ${
                            (uploadingImage || isPointsInsufficient) 
                              ? 'bg-gray-300 cursor-not-allowed' 
                              : ''
                          }`}
                          style={{
                            background: (uploadingImage || isPointsInsufficient) 
                              ? '#d1d5db' 
                              : GlobalStyles.colors.primary
                          }}
                        >
                          {loading ? (
                            <span className="flex items-center gap-2">
                              <span className="animate-spin">⏳</span>
                              生成中...
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <span>🎬</span>
                                    {uploadingImage ? '参考图上传中...' : (isPointsInsufficient ? '积分不足' : '开始生成')}
                            </span>
                          )}
                        </Button>
                            </>
                          );
                        })()}
                      </div>
                  </div>

                  {/* 参数设置卡片 - 应用统一样式 */}
                  <div className="unified-card">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-lg">⚙️</span>
                      <Text className="unified-text-primary font-bold">生成参数</Text>
                    </div>
                        <div className="flex items-center gap-3 flex-nowrap overflow-x-auto">
                          {/* 视频比例选择 */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-sm text-gray-600 font-semibold">比例:</span>
                            <Button
                              type="default"
                              size="small"
                              className="h-10 px-4 rounded-xl text-sm font-semibold bg-white text-gray-700 border-2 border-gray-200 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 hover:border-blue-200 whitespace-nowrap shadow-lg transition-all duration-300 hover:scale-105"
                              onClick={() => {
                                // 切换比例
                                setOrientation(orientation === 'PORTRAIT' ? 'LANDSCAPE' : 'PORTRAIT');
                              }}
                            >
                              {orientation === 'PORTRAIT' ? '9:16' : '16:9'}
                            </Button>
                          </div>

                          {/* 视频时长 */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-sm text-gray-600 font-semibold">时长:</span>
                          <Button
                            type="default"
                            size="small"
                              className="h-10 px-4 rounded-xl text-sm font-semibold bg-white text-gray-700 border-2 border-gray-200 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 hover:border-blue-200 whitespace-nowrap shadow-lg transition-all duration-300 hover:scale-105"
                            onClick={() => {
                              // 切换时长
                              setDuration(duration === 10 ? 30 : 10);
                            }}
                          >
                            {duration}秒
                          </Button>
                          </div>

                          {/* AI模型选择 */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-sm text-gray-600 font-semibold whitespace-nowrap">模型:</span>
                            <Select
                              value={selectedModel}
                              onChange={setSelectedModel}
                            size="small"
                              className="min-w-[160px] max-w-[200px]"
                              style={{ height: '40px' }}
                              loading={loadingProviders || loadingModels}
                              placeholder={videoModels.length === 0 ? "请管理员配置API服务商" : "选择模型"}
                              disabled={videoModels.length === 0}
                              styles={{ popup: { root: { minWidth: 200 } } }}
                            >
                              {videoModels.length === 0 ? (
                                <Select.Option value="" disabled>
                                  <div className="flex items-center gap-2 text-gray-400">
                                    <InfoCircleOutlined />
                                    <span className="text-sm">请管理员在控制台配置API服务商</span>
                        </div>
                                </Select.Option>
                              ) : (
                                videoModels.map((model) => {
                                  // 根据当前生成类型动态计算积分消耗
                                  let currentCost = model.cost;
                                  if (model.creditCosts) {
                                    if (generationType === 'TEXT_TO_VIDEO') {
                                      currentCost = model.creditCosts.textToVideo || model.cost;
                                    } else if (generationType === 'IMAGE_TO_VIDEO') {
                                      currentCost = model.creditCosts.imageToVideo || model.cost;
                                    } else if (generationType === 'VIDEO_TO_VIDEO') {
                                      currentCost = model.creditCosts.videoToVideo || model.creditCosts.imageToVideo || model.cost;
                                    }
                                  }
                                  
                                  return (
                                    <Select.Option key={model.id} value={model.id}>
                                      <div className="flex items-center justify-between w-full py-1">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                          <span className="font-medium text-gray-800 text-sm truncate">{model.name}</span>
                                          <span className="text-xs text-gray-500 truncate">({model.providerName})</span>
                                        </div>
                                        <span className="text-xs text-blue-500 font-semibold ml-2 flex-shrink-0">{currentCost}积分</span>
                                      </div>
                                    </Select.Option>
                                  );
                                })
                              )}
                            </Select>
                          </div>
                        </div>
                  </div>

                </Form>
              </motion.div>
            </div>

            {/* 右侧：生成预览 - 占1列 (1/3) */}
            <div className="lg:col-span-1 hidden md:block">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-6 flex flex-col"
              >
                  <div className="flex items-center justify-between mb-3 py-0 min-h-[40px]">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">🎬</span>
                      <Title level={4} className="!mb-0 font-bold">生成预览</Title>
                    </div>
                    <Space>
                      <Button 
                        icon={<HistoryOutlined />}
                        onClick={() => navigate('/history')}
                        size="small"
                        className="rounded-lg shadow-sm hover:shadow-md transition-all"
                      >
                        <span className="hidden md:inline">历史</span>
                        <span className="md:hidden">历史记录</span>
                      </Button>
                    </Space>
                  </div>

                <div className="flex-1 flex flex-col -mt-2">
                <AnimatePresence mode="wait">
                    {!currentGeneration ? (
                      <motion.div
                        key="empty"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="text-center py-16 px-8 flex-1 flex flex-col justify-center"
                      >
                          <motion.div
                            animate={{ 
                            y: [0, -10, 0],
                            scale: [1, 1.05, 1],
                            }}
                            transition={{ 
                            duration: 3,
                              repeat: Infinity,
                              repeatType: "reverse"
                            }}
                          className="inline-block text-8xl"
                          >
                          🎬✨
                          </motion.div>
                        <Title level={3} className="text-gray-600 mb-4 font-bold">
                          开启你的创作之旅
                        </Title>
                        <Text type="secondary" className="text-lg mb-6 max-w-md">
                          输入你的创意灵感，让AI为你生成精彩的视频内容
                        </Text>
                        <div className="flex items-center gap-2 justify-center flex-nowrap">
                          <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded-full text-xs whitespace-nowrap">🎨创意无限</span>
                          <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-xs whitespace-nowrap">⚡快速生成</span>
                          <span className="px-2 py-1 bg-pink-100 text-pink-600 rounded-full text-xs whitespace-nowrap">🎯精准控制</span>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="results"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="space-y-4 flex-1 flex flex-col"
                      >
                        {/* 视频展示区域 */}
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8, y: 20 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          transition={{ duration: 0.4 }}
                          className="group relative"
                          style={{ marginTop: '24px' }}
                        >
                          {(() => {
                            console.log('🎭 [渲染] 检查状态:', {
                              status: currentGeneration.status,
                              isFailed: currentGeneration.status === 'failed',
                              errorMessage: currentGeneration.errorMessage
                            })
                            return currentGeneration.status === 'failed'
                          })() ? (
                            // ❌ 失败提示卡片（立即停止预览动画）
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl min-h-[220px] bg-red-50 border border-red-200 flex items-center justify-center p-6 text-center">
                              <div>
                                <div className="text-4xl mb-3">⚠️</div>
                                <div className="text-red-600 font-semibold mb-2">生成失败</div>
                                <div className="text-red-500 text-sm mb-4">
                                  {currentGeneration.errorMessage || '生成失败，请更换参考图或稍后重试'}
                                </div>
                                <div className="flex items-center justify-center gap-2">
                                  <Button onClick={() => form.submit()} type="primary" size="small">重新生成</Button>
                                  <Button onClick={() => navigate('/history')} size="small">前往历史</Button>
                                </div>
                              </div>
                            </div>
                          ) : (currentGeneration.status === 'GENERATING' || currentGeneration.status === 'PENDING' || currentGeneration.status === 'PROCESSING' || !currentGeneration.outputVideo) ? (
                            // 🎬 生成中预览卡片 - 美化版
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl min-h-[280px]">
                              {/* 动态渐变背景 */}
                              <div className="absolute inset-0 bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 opacity-80 animate-pulse"></div>
                              <div className="absolute inset-0 bg-gradient-to-tr from-yellow-300/30 via-transparent to-blue-300/30 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                              
                              {/* 星光粒子效果 */}
                              <div className="absolute inset-0 overflow-hidden">
                                <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full animate-ping opacity-75"></div>
                                <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-yellow-200 rounded-full animate-ping opacity-60" style={{ animationDelay: '0.3s' }}></div>
                                <div className="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-pink-200 rounded-full animate-ping opacity-70" style={{ animationDelay: '0.6s' }}></div>
                              </div>
                              
                              {/* 内容区 */}
                              <div className="relative backdrop-blur-sm bg-white/20 flex flex-col items-center justify-center p-8 text-center h-full min-h-[280px]">
                                {/* 电影场记板图标 + 旋转动画 */}
                                <div className="relative mb-5">
                                  <div className="w-14 h-14 bg-gradient-to-br from-white to-gray-100 rounded-2xl shadow-lg flex items-center justify-center transform hover:scale-110 transition-transform duration-300">
                                    <span className="text-3xl animate-bounce">🎬</span>
                                  </div>
                                  {/* 环形进度 */}
                                  <div className="absolute -inset-2 border-3 border-white/40 border-t-white rounded-full animate-spin"></div>
                                </div>
                                
                                {/* 主标题 - 大字强调 */}
                                <div className="text-xl font-bold text-white mb-2 drop-shadow-lg">
                                  ✨ 正在为你编织画面
                                </div>
                                
                                {/* 副标题 - 沉浸式文案 */}
                                <div className="text-sm text-white/95 mb-3 font-medium drop-shadow">
                                  🎬 AI 正在导演你的专属短片
                                </div>
                                
                                {/* 等待时间 - 轻松化 */}
                                <div className="text-xs text-white/85 mb-3 flex items-center gap-1.5">
                                  <span className="animate-pulse text-base">⏳</span>
                                  <span>片刻等待，即将呈现</span>
                                </div>
                                
                                {/* 底部提示 - 简化突出 */}
                                <div className="mt-2 px-3 py-1.5 bg-white/95 rounded-full text-xs text-indigo-600 font-medium shadow-lg backdrop-blur">
                                  💡 预览将自动保存至历史记录
                                </div>
                              </div>
                            </div>
                          ) : (
                            // 完成的结果卡片
                            <div className="relative rounded-lg overflow-hidden bg-white shadow-md">
                              <video 
                                controls 
                                className="w-full h-auto block"
                                style={{ 
                                  objectFit: 'cover',
                                  display: 'block',
                                  backgroundColor: 'transparent'
                                }}
                                autoPlay
                                poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 450'%3E%3Crect fill='%23ffffff' width='800' height='450'/%3E%3C/svg%3E"
                              >
                                <source src={currentGeneration.outputVideo} type="video/mp4" />
                              </video>
                            </div>
                          )}
                        </motion.div>

                        {/* 底部操作按钮（与图片页一致：始终显示 下载/重新生成；下载在无结果时禁用） */}
                        <div className="flex justify-center space-x-3 mt-4">
                          <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            disabled={!currentGeneration.outputVideo}
                            onClick={async () => {
                              try {
                                message.loading('正在下载视频...', 0)
                                
                                // 使用 fetch 下载，避免页面跳转
                                const response = await fetch(currentGeneration.outputVideo)
                                const blob = await response.blob()
                                
                                // 创建下载链接
                                const url = window.URL.createObjectURL(blob)
                                const link = document.createElement('a')
                                link.href = url
                                link.download = `video-${Date.now()}.mp4`
                                link.style.display = 'none'
                                
                                // 添加到 DOM，点击，然后移除
                                document.body.appendChild(link)
                                link.click()
                                document.body.removeChild(link)
                                
                                // 释放 URL 对象
                                window.URL.revokeObjectURL(url)
                                
                                message.destroy()
                                message.success('下载成功')
                              } catch (error) {
                                message.destroy()
                                message.error('下载失败，请重试')
                                console.error('下载失败:', error)
                              }
                            }}
                            className="rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            下载视频
                          </Button>
                          <Button
                            icon={<ReloadOutlined />}
                            onClick={() => form.submit()}
                            className="rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            重新生成
                          </Button>
                        </div>
                      </motion.div>
                    )}
                </AnimatePresence>
                </div>

                  {/* 创作提示卡片 - 独立模块 */}
                  <div className="mt-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-200 shadow-md hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl">💡</span>
                      <Text className="text-sm font-bold text-amber-800">创作提示</Text>
                    </div>
                    <ul className="text-sm text-amber-700 space-y-2.5 mb-0 leading-relaxed">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">✓</span>
                        <span>描述要具体、生动，包含场景、光线等细节</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">✓</span>
                        <span>避免使用版权人物、品牌名称</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">✓</span>
                        <span>添加艺术风格可获得更好效果（如：电影感）</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">✓</span>
                        <span>简单场景生成速度更快</span>
                      </li>
                    </ul>
                  </div>
              </motion.div>
              </div>
            </div>
          </div>

            {/* 底部创作风格区域 - 移动端隐藏 */}
            <div className="w-full mt-20 hidden md:block">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <div className="text-center mb-10">
              <Title level={2} className="mb-3">
                <span className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 bg-clip-text text-transparent">
                  ✨ 热门创作风格
                </span>
              </Title>
              <Text type="secondary" className="text-lg text-gray-600">💡 点击卡片快速开始创作，激发你的灵感</Text>
            </div>
            
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
              {[
                { 
                  style: '自然风光',
                  prompt: '日落时分的海滩，海浪轻拍沙滩，海鸥飞过，金色阳光洒在水面上，电影级慢镜头',
                  emoji: '🌅',
                  gradient: 'from-blue-50 to-cyan-50',
                  borderColor: 'border-blue-200'
                },
                {
                  style: '科幻场景',
                  prompt: '未来城市夜景，霓虹灯闪烁，飞行汽车穿梭，赛博朋克风格，雨夜反光',
                  emoji: '🌃',
                  gradient: 'from-purple-50 to-pink-50',
                  borderColor: 'border-purple-200'
                },
                {
                  style: '动物世界',
                  prompt: '一只可爱的小熊猫在竹林里玩耍，咀嚼竹叶，憨态可掬，4K高清，自然纪录片风格',
                  emoji: '🐼',
                  gradient: 'from-green-50 to-emerald-50',
                  borderColor: 'border-green-200'
                },
                {
                  style: '旅行记录',
                  prompt: '城市街头手持镜头行走拍摄，快速剪辑，转场流畅，音乐节奏感强',
                  emoji: '🎬',
                  gradient: 'from-orange-50 to-red-50',
                  borderColor: 'border-orange-200'
                }
              ].map((example, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -8, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <button 
                    type="button"
                    role="button"
                    tabIndex={0}
                    className={`h-full cursor-pointer rounded-3xl border-2 ${example.borderColor} bg-gradient-to-br ${example.gradient} shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden p-8 group relative z-10 pointer-events-auto w-full text-left`}
                    onClick={() => {
                      setMobileTab('create')
                      setPromptValue(example.prompt)
                      try { form.setFieldsValue({ prompt: example.prompt }) } catch {}
                      try { form.scrollToField('prompt', { block: 'center' }) } catch {}
                      setTimeout(() => { try { promptInputRef?.current?.focus?.() } catch {} }, 0)
                      message.success(`已应用"${example.style}"风格 ✨`)
                    }}
                    title={`点击应用：${example.prompt}`}
                  >
                    <div className="text-center">
                      <motion.div 
                        className="text-7xl mb-5 pointer-events-none"
                        whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                        transition={{ duration: 0.5 }}
                      >
                        {example.emoji}
                      </motion.div>
                      <Title level={4} className="!mb-3 font-bold text-gray-800 pointer-events-none">{example.style}</Title>
                      <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-white group-hover:bg-gradient-to-r group-hover:from-blue-50 group-hover:to-purple-50 rounded-full text-sm font-semibold text-gray-700 group-hover:text-blue-700 shadow-md mb-4 border border-gray-200 group-hover:border-blue-300 transition-all duration-300 pointer-events-none">
                        <span className="text-base">✨</span>
                        <span>点击应用</span>
                      </div>
                      <Paragraph 
                        ellipsis={{ rows: 3 }} 
                        className="text-sm text-gray-600 mb-0 leading-relaxed pointer-events-none"
                      >
                      {example.prompt}
                      </Paragraph>
                    </div>
                  </button>
                </motion.div>
              ))}
            </div>
          </motion.div>
            </div>
        </motion.div>
      </div>
    </div>
  )
}

export default VideoGenerationPage
